import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getUsers,
  blockUser,
  deleteUser,
  bulkBlockUsers,
  bulkVerifyUsers,
  bulkDeleteUsers,
  activateTrial,
  extendTrial,
  convertTrial,
  postSubscriptionRevoke,
  AdminUser,
  UsersListResponse,
  BulkFilter,
} from '../adminApi';
import { useAdmin } from '@/contexts/AdminContext';

type SortField = 'email' | 'created_at' | 'subscription_status' | 'is_verified';
type SortOrder = 'ASC' | 'DESC';

export default function AdminUsersPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
  const [filterSubscription, setFilterSubscription] = useState('');
  const [filterVerified, setFilterVerified] = useState('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'block' | 'verify' | 'delete' | null>(null);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; userId: string; userEmail: string }>({
    open: false, userId: '', userEmail: '',
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Subscription management modal
  const [subModal, setSubModal] = useState<{ open: boolean; userId: string; userEmail: string }>({
    open: false, userId: '', userEmail: '',
  });
  const [subAction, setSubAction] = useState<'trial' | 'extend' | 'convert' | 'revoke' | null>(null);
  const [subDays, setSubDays] = useState(7);
  const [subPlan, setSubPlan] = useState('premium');
  const [subLoading, setSubLoading] = useState(false);
  const [subMessage, setSubMessage] = useState('');

  // Toast notification
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastTimeout = useRef<ReturnType<typeof setTimeout>>();

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(() => setToast(null), 3000);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(() => {
    getUsers({
      page: pagination.page,
      limit: pagination.limit,
      search: debouncedSearch,
      subscription: filterSubscription || undefined,
      verified: filterVerified || undefined,
      sortBy,
      sortOrder,
    })
      .then((res: UsersListResponse) => {
        setUsers(res.users);
        setPagination({ page: res.page, totalPages: res.totalPages, total: res.total, limit: res.limit });
        setSelectedIds(new Set());
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.limit, debouncedSearch, filterSubscription, filterVerified, sortBy, sortOrder]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchUsers();
  }, [isAuthenticated, navigate, fetchUsers]);

  // Reset to page 1 on filter change
  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [debouncedSearch, filterSubscription, filterVerified, sortBy, sortOrder]);

  const handleBlock = async (id: string, currentlyActive: boolean) => {
    try {
      await blockUser(id, currentlyActive, 'Blocked from admin panel');
      showToast(currentlyActive ? 'Пользователь заблокирован' : 'Пользователь разблокирован');
      fetchUsers();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    }
  };

  const confirmDelete = (id: string, email: string) => {
    setDeleteModal({ open: true, userId: id, userEmail: email });
  };

  const executeDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteUser(deleteModal.userId);
      setDeleteModal({ open: false, userId: '', userEmail: '' });
      showToast('Пользователь удалён');
      fetchUsers();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openSubModal = (id: string, email: string) => {
    setSubModal({ open: true, userId: id, userEmail: email });
    setSubAction(null);
    setSubDays(7);
    setSubPlan('premium');
    setSubMessage('');
  };

  const executeSubAction = async () => {
    if (!subAction) return;
    setSubLoading(true);
    setSubMessage('');
    try {
      if (subAction === 'trial') {
        const res = await activateTrial(subModal.userId, subDays, 'Активировано из админ-панели');
        setSubMessage(`Триал активирован до ${new Date(res.data.trialEndDate).toLocaleDateString('ru-RU')}`);
      } else if (subAction === 'extend') {
        const res = await extendTrial(subModal.userId, subDays, 'Продлено из админ-панели');
        setSubMessage(`Триал продлён до ${new Date(res.data.trialEndDate).toLocaleDateString('ru-RU')}`);
      } else if (subAction === 'convert') {
        const res = await convertTrial(subModal.userId, subPlan, subDays * 30, 'Конвертировано из админ-панели');
        setSubMessage(`Подписка ${subPlan} активна до ${new Date(res.data.subscriptionEndDate).toLocaleDateString('ru-RU')}`);
      } else if (subAction === 'revoke') {
        await postSubscriptionRevoke({ email: subModal.userEmail });
        setSubMessage('Подписка отозвана');
      }
      showToast(subMessage || 'Действие выполнено');
      fetchUsers();
    } catch (err) {
      const msg = 'Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка');
      setSubMessage(msg);
      showToast(msg, 'error');
    } finally {
      setSubLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u.id)));
    }
  };

  const openBulkModal = (action: 'block' | 'verify' | 'delete') => {
    setBulkAction(action);
    setBulkReason('');
    setBulkModalOpen(true);
  };

  const executeBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const filter: BulkFilter = { userIds: Array.from(selectedIds) };
      if (bulkAction === 'block') {
        const res = await bulkBlockUsers(filter, bulkReason);
        showToast(`Заблокировано: ${res.data.affected}`);
      } else if (bulkAction === 'verify') {
        const res = await bulkVerifyUsers(filter, bulkReason);
        showToast(`Верифицировано: ${res.data.affected}`);
      } else if (bulkAction === 'delete') {
        const res = await bulkDeleteUsers(filter, bulkReason);
        showToast(`Удалено: ${res.data.affected}`);
      }
      setBulkModalOpen(false);
      fetchUsers();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortBy !== field) return '↕';
    return sortOrder === 'ASC' ? '↑' : '↓';
  };

  const subBadge = (status: string) => {
    const colors: Record<string, string> = {
      free: 'bg-slate-600 text-slate-200',
      base: 'bg-teal-600/30 text-teal-300',
      premium: 'bg-purple-600/30 text-purple-300',
      pro: 'bg-amber-600/30 text-amber-300',
      trial: 'bg-blue-600/30 text-blue-300',
      active: 'bg-green-600/30 text-green-300',
    };
    return colors[status] || 'bg-slate-600 text-slate-200';
  };

  const thClass = 'px-4 py-3 font-medium cursor-pointer hover:text-white transition select-none';

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg text-sm font-medium shadow-lg transition-all ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-xl font-bold text-white hover:text-purple-400 transition">← Назад</Link>
          <h1 className="text-xl font-bold text-white">Пользователи ({pagination.total})</h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по email или имени..."
            className="flex-1 min-w-48 px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <select
            value={filterSubscription}
            onChange={(e) => setFilterSubscription(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Все подписки</option>
            <option value="free">Free</option>
            <option value="base">Base</option>
            <option value="premium">Premium</option>
            <option value="pro">Pro</option>
            <option value="trial">Trial</option>
          </select>
          <select
            value={filterVerified}
            onChange={(e) => setFilterVerified(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Все</option>
            <option value="true">Верифицированные</option>
            <option value="false">Неверифицированные</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <span className="text-sm text-slate-400">Выбрано: {selectedIds.size}</span>
            <button onClick={() => openBulkModal('block')} className="px-3 py-1.5 text-xs bg-amber-600/20 text-amber-400 rounded-lg hover:bg-amber-600/40 transition">
              Заблокировать
            </button>
            <button onClick={() => openBulkModal('verify')} className="px-3 py-1.5 text-xs bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/40 transition">
              Верифицировать
            </button>
            <button onClick={() => openBulkModal('delete')} className="px-3 py-1.5 text-xs bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/40 transition">
              Удалить
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                      <th className="px-4 py-3 w-10">
                        <input type="checkbox" checked={users.length > 0 && selectedIds.size === users.length} onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500" />
                      </th>
                      <th className={thClass} onClick={() => handleSort('email')}>Email {sortIcon('email')}</th>
                      <th className="px-4 py-3 font-medium">Имя</th>
                      <th className={thClass} onClick={() => handleSort('subscription_status')}>Подписка {sortIcon('subscription_status')}</th>
                      <th className={thClass} onClick={() => handleSort('is_verified')}>Вериф. {sortIcon('is_verified')}</th>
                      <th className={thClass} onClick={() => handleSort('created_at')}>Дата {sortIcon('created_at')}</th>
                      <th className="px-4 py-3 font-medium">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500">Пользователи не найдены</td></tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selectedIds.has(user.id)} onChange={() => toggleSelect(user.id)}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500" />
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/admin/users/${user.id}`} className="text-blue-400 hover:underline text-sm">{user.email}</Link>
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm">{user.full_name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${subBadge(user.subscription_status)}`}>
                              {user.subscription_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-sm">{user.is_verified ? '✅' : '❌'}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{new Date(user.created_at).toLocaleDateString('ru-RU')}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              <button onClick={() => handleBlock(user.id, true)}
                                className="text-xs px-2 py-1 bg-amber-600/20 text-amber-400 rounded hover:bg-amber-600/40 transition">Блок</button>
                              <button onClick={() => openSubModal(user.id, user.email)}
                                className="text-xs px-2 py-1 bg-purple-600/20 text-purple-400 rounded hover:bg-purple-600/40 transition">Подписка</button>
                              <button onClick={() => confirmDelete(user.id, user.email)}
                                className="text-xs px-2 py-1 bg-red-600/20 text-red-400 rounded hover:bg-red-600/40 transition">Удалить</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-sm text-slate-400">Страница {pagination.page} из {pagination.totalPages}</span>
                  <div className="flex gap-2">
                    <button disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition">←</button>
                    <button disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition">→</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-red-600/30 p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-red-400 mb-3">Удаление пользователя</h2>
            <p className="text-slate-300 mb-2">Вы уверены, что хотите удалить:</p>
            <p className="text-white font-medium mb-4">{deleteModal.userEmail}</p>
            <p className="text-red-400 text-sm mb-6">Это действие нельзя отменить.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteModal({ open: false, userId: '', userEmail: '' })}
                className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition">Отмена</button>
              <button onClick={executeDelete} disabled={deleteLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {deleteLoading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Management Modal */}
      {subModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-purple-600/30 p-6 w-full max-w-lg">
            <h2 className="text-lg font-bold text-purple-400 mb-2">Управление подпиской</h2>
            <p className="text-slate-400 text-sm mb-5">{subModal.userEmail}</p>
            {subMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${subMessage.startsWith('Ошибка') ? 'bg-red-600/20 text-red-400' : 'bg-green-600/20 text-green-400'}`}>
                {subMessage}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button onClick={() => setSubAction('trial')}
                className={`p-3 rounded-lg border text-sm font-medium transition ${subAction === 'trial' ? 'bg-blue-600/30 border-blue-500 text-blue-300' : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'}`}>
                🎁 Активировать триал
              </button>
              <button onClick={() => setSubAction('extend')}
                className={`p-3 rounded-lg border text-sm font-medium transition ${subAction === 'extend' ? 'bg-blue-600/30 border-blue-500 text-blue-300' : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'}`}>
                ⏰ Продлить триал
              </button>
              <button onClick={() => setSubAction('convert')}
                className={`p-3 rounded-lg border text-sm font-medium transition ${subAction === 'convert' ? 'bg-purple-600/30 border-purple-500 text-purple-300' : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'}`}>
                💎 Конвертировать
              </button>
              <button onClick={() => setSubAction('revoke')}
                className={`p-3 rounded-lg border text-sm font-medium transition ${subAction === 'revoke' ? 'bg-red-600/30 border-red-500 text-red-300' : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'}`}>
                🚫 Отозвать подписку
              </button>
            </div>
            {subAction && subAction !== 'revoke' && (
              <div className="space-y-4 mb-5">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Количество дней</label>
                  <input type="number" value={subDays} onChange={(e) => setSubDays(parseInt(e.target.value) || 1)} min={1} max={365}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
                {subAction === 'convert' && (
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Тариф</label>
                    <select value={subPlan} onChange={(e) => setSubPlan(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="base">Base</option>
                      <option value="premium">Premium</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setSubModal({ open: false, userId: '', userEmail: '' })}
                className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition">Закрыть</button>
              {subAction && (
                <button onClick={executeSubAction} disabled={subLoading}
                  className={`px-4 py-2 text-sm rounded-lg transition disabled:opacity-50 ${subAction === 'revoke' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>
                  {subLoading ? 'Выполняется...' : 'Выполнить'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Modal */}
      {bulkModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-white mb-4">
              {bulkAction === 'block' ? 'Заблокировать' : bulkAction === 'verify' ? 'Верифицировать' : 'Удалить'} ({selectedIds.size})
            </h2>
            <textarea value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} placeholder="Причина (необязательно)"
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4" rows={3} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setBulkModalOpen(false)}
                className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition">Отмена</button>
              <button onClick={executeBulkAction} disabled={bulkLoading}
                className={`px-4 py-2 text-sm rounded-lg transition disabled:opacity-50 ${
                  bulkAction === 'delete' ? 'bg-red-600 text-white hover:bg-red-700' :
                  bulkAction === 'block' ? 'bg-amber-600 text-white hover:bg-amber-700' :
                  'bg-green-600 text-white hover:bg-green-700'
                }`}>
                {bulkLoading ? 'Выполняется...' : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
